import {
  BestPromotion,
  FormatedPrice,
  Item,
  ItemVisibilityReason,
  ItemVisibilityState,
  PromotionLite,
} from '@eshop/types/catalogue.types.ts';
import { getFormattedPrice, parsePrice, type ParsedPrice } from '@tools/utils/common.tsx';

export interface ItemFilterOptions {
  erpEnabled?: boolean;
}

const coerceFlag = (value: boolean | null | undefined, fallback: boolean): boolean => {
  if (value === null || value === undefined) {
    return fallback;
  }
  return Boolean(value);
};

const buildReasons = (visibility: Omit<ItemVisibilityState, 'reasons'>): ItemVisibilityReason[] => {
  const reasons: ItemVisibilityReason[] = [];
  if (!visibility.isActive) reasons.push('inactive');
  if (!visibility.isVisibleWeb) reasons.push('hidden');
  if (!visibility.isForSale) reasons.push('not_for_sale');
  if (visibility.isInternal) {
    reasons.push('internal');
  } else if (visibility.blockedByErp) {
    reasons.push('erp_disabled');
  }
  return reasons;
};

const computeVisibility = (item: Item, options?: ItemFilterOptions): ItemVisibilityState => {
  const isActive = coerceFlag(item.is_active, true);
  const isVisibleWeb = coerceFlag(item.is_visible_web, true);
  const isForSale = coerceFlag(item.is_for_sale, true);
  const isInternal = Boolean(item.is_internal);
  const servicePublicable = item.service_publicable ?? true;
  const erpEnabled = options?.erpEnabled ?? item.extra_info?.erp_enabled ?? true;
  const requiresErp = isInternal || (item.item_type === 'service' && servicePublicable === false);
  const blockedByErp = !erpEnabled && requiresErp;
  const isPublishable = isActive && isVisibleWeb && isForSale && !blockedByErp;

  const baseVisibility = {
    isActive,
    isVisibleWeb,
    isForSale,
    isInternal,
    erpEnabled,
    blockedByErp,
    isPublishable,
  } satisfies Omit<ItemVisibilityState, 'reasons'>;

  return {
    ...baseVisibility,
    reasons: buildReasons(baseVisibility),
  };
};

const areVisibilityEqual = (
  current: ItemVisibilityState | undefined,
  next: ItemVisibilityState,
): boolean => {
  if (!current) return false;
  return (
    current.isActive === next.isActive &&
    current.isVisibleWeb === next.isVisibleWeb &&
    current.isForSale === next.isForSale &&
    current.isInternal === next.isInternal &&
    current.erpEnabled === next.erpEnabled &&
    current.blockedByErp === next.blockedByErp &&
    current.isPublishable === next.isPublishable &&
    current.reasons.length === next.reasons.length &&
    current.reasons.every((reason, index) => reason === next.reasons[index])
  );
};

export const filterItem = (item: Item, options?: ItemFilterOptions): Item => {
  const visibility = computeVisibility(item, options);
  if (areVisibilityEqual(item.visibility, visibility)) {
    return item;
  }

  return {
    ...item,
    visibility,
  };
};

export const filterItems = (items: Item[], options?: ItemFilterOptions): Item[] =>
  items.map((item) => filterItem(item, options));

const getFirstChildPriceSource = (item: Item): Item | null => {
  const children = Array.isArray(item.children) ? item.children : [];
  let selected: Item | null = null;
  let selectedValue: number | null = null;

  children.forEach((child) => {
    const parsed = parsePrice(child.price);
    if (!parsed || parsed.value <= 0) return;
    if (selectedValue === null || parsed.value < selectedValue) {
      selected = child as Item;
      selectedValue = parsed.value;
    }
  });

  return selected;
};

export interface VariantPriceRange {
  min: number;
  max: number;
  currency: string;
  formattedMin: string;
  formattedMax: string;
  formattedRange: string;
}

export const getVariantPriceRange = (item: Item): VariantPriceRange | null => {
  const children = Array.isArray(item.children) ? item.children : [];
  const prices = children
    .map((child) => parsePrice(child.price))
    .filter((price): price is ParsedPrice => price !== null && price.value > 0);

  if (prices.length < 2) return null;

  const uniqueValues = new Map<number, ParsedPrice>();
  prices.forEach((price) => {
    uniqueValues.set(price.value, price);
  });

  if (uniqueValues.size < 2) return null;

  const sortedPrices = Array.from(uniqueValues.values()).sort((a, b) => a.value - b.value);
  const minPrice = sortedPrices[0];
  const maxPrice = sortedPrices[sortedPrices.length - 1];
  const currency = minPrice.currency || maxPrice.currency || 'ARS';
  const formatRangePrice = (value: number): string =>
    getFormattedPrice(value, currency).replace(/\u00a0/g, '');
  const formattedMin = formatRangePrice(minPrice.value);
  const formattedMax = formatRangePrice(maxPrice.value);

  return {
    min: minPrice.value,
    max: maxPrice.value,
    currency,
    formattedMin,
    formattedMax,
    formattedRange: `${formattedMin} - ${formattedMax}`,
  };
};

/** @deprecated Promotional prices are backend-owned. Keep only for explicit legacy fallbacks. */
export const calculatePromotionPrice = (
  basePrice: number,
  promotion: PromotionLite,
): { discountedPrice: number; discount: number } => {
  if (!promotion.type || !promotion.value) {
    return { discountedPrice: basePrice, discount: 0 };
  }

  let discount = 0;
  if (promotion.type === 'percent') {
    discount = basePrice * (promotion.value / 100);
  } else if (promotion.type === 'fixed') {
    discount = Math.min(promotion.value, basePrice);
  } else if (promotion.type === 'final_price') {
    discount = Math.max(0, basePrice - promotion.value);
  }

  return {
    discountedPrice: Math.max(0, basePrice - discount),
    discount,
  };
};

/** @deprecated Promotional prices are backend-owned. Keep only for explicit legacy fallbacks. */
export const getBestPromotion = (
  basePrice: number,
  currency: string,
  promotions: PromotionLite[],
): BestPromotion | undefined => {
  if (!promotions || promotions.length === 0) return undefined;

  let bestPromotion;
  let maxDiscount = 0;
  let bestPrice = basePrice;

  for (const promotion of promotions) {
    const { discountedPrice, discount } = calculatePromotionPrice(basePrice, promotion);
    if (discount > maxDiscount) {
      maxDiscount = discount;
      bestPrice = discountedPrice;
      bestPromotion = promotion;
    }
  }

  return bestPromotion && {
    promotion: bestPromotion,
    discountedPrice: bestPrice,
    formatedDiscountedPrice: getFormattedPrice(bestPrice, currency),
    discount: maxDiscount,
    formatedDiscount: getFormattedPrice(maxDiscount, currency),
  };
};

export const getItemPrice = (item: Item): FormatedPrice | null => {
  const sourceItem = parsePrice(item.price) ? item : (getFirstChildPriceSource(item) ?? item);
  const parsed = parsePrice(sourceItem.price);
  if (!parsed) return null;

  const rawPrice = typeof sourceItem.price === 'object' && sourceItem.price !== null
    ? (sourceItem.price as Partial<FormatedPrice> & { promotion?: BestPromotion })
    : undefined;

  const formattedPrice = typeof rawPrice?.formattedPrice === 'string'
    ? rawPrice.formattedPrice
    : getFormattedPrice(parsed.value, parsed.currency);

  const backendFinalPrice = typeof rawPrice?.final_price === 'number'
    ? rawPrice.final_price
    : undefined;
  const backendDiscount = typeof rawPrice?.discount_amount === 'number'
    ? rawPrice.discount_amount
    : undefined;
  const backendPromotionId = typeof rawPrice?.promotion_id === 'number'
    ? rawPrice.promotion_id
    : undefined;
  const rawPromotionPayload = typeof rawPrice?.promotion === 'object' && rawPrice.promotion !== null
    ? rawPrice.promotion as { name?: unknown; display_type?: unknown; conditional_price?: unknown }
    : undefined;
  const backendDisplayType = rawPromotionPayload?.display_type === 'conditional_catalog_price'
    ? 'conditional_catalog_price'
    : 'static_catalog_price';
  const backendConditionalPrice = rawPrice?.conditional_price === true
    || rawPromotionPayload?.conditional_price === true
    || backendDisplayType === 'conditional_catalog_price';
  const staticPromotion = sourceItem.promotions?.find((candidate) => (
    candidate.display_type === 'static_catalog_price' ||
    candidate.display_type === 'conditional_catalog_price' ||
    (backendPromotionId !== undefined && candidate.id === backendPromotionId)
  ));
  const backendPromotion = backendFinalPrice !== undefined
    ? {
      promotion: staticPromotion ?? {
        id: backendPromotionId ?? 0,
        name: rawPromotionPayload
          ? String(rawPromotionPayload.name ?? 'Promocion')
          : 'Promocion',
        origin: 'catalog',
        display_type: backendDisplayType,
        requires_cart_state: backendConditionalPrice,
        conditional_price: backendConditionalPrice,
        final_price: backendFinalPrice,
        discount_amount: backendDiscount,
      },
      discountedPrice: backendFinalPrice,
      formatedDiscountedPrice: getFormattedPrice(backendFinalPrice, parsed.currency),
      discount: backendDiscount ?? Math.max(0, parsed.value - backendFinalPrice),
      formatedDiscount: getFormattedPrice(
        backendDiscount ?? Math.max(0, parsed.value - backendFinalPrice),
        parsed.currency,
      ),
      conditional_price: backendConditionalPrice,
    }
    : undefined;

  const promotion = backendPromotion ?? rawPrice?.promotion;
  const discountedValue = promotion?.discountedPrice ?? parsed.value;
  const formattedDiscounted = promotion?.formatedDiscountedPrice
    ?? getFormattedPrice(discountedValue, parsed.currency);

  return {
    value: parsed.value,
    formattedPrice,
    currency: parsed.currency,
    promotion,
    base_price_at_calculation: rawPrice?.base_price_at_calculation,
    discount_amount: backendDiscount,
    final_price: backendFinalPrice,
    promotion_id: backendPromotionId,
    conditional_price: backendConditionalPrice,
    offer: promotion
      ? { finalFormatedOfferPrice: formattedDiscounted }
      : undefined,
  };
};
